import { gql } from "@apollo/client";
import { POLESET_FRAGMENT } from "./gqlPoleset";

export const PROJECT_FRAGMENT = gql`
  ${POLESET_FRAGMENT}
  fragment Project on Project {
    id
    poles {
      identifier
      position {
        x
        y
        z
      }
      direction {
        x
        y
        z
      }
      length
    }
    lashings {
      identifier
      fixedPoleIdentifier
      loosePoleIdentifier
    }
    bipodLashings {
      identifier
      pole1Identifier
      pole2Identifier
    }
    tripodLashings {
      identifier
      leftPoleIdentifier
      middlePoleIdentifier
      rightPoleIdentifier
    }
    scaffoldLashings {
      identifier
      pole1Identifier
      pole2Identifier
      offset
    }
    poleset {
      ...Poleset
    }
  }
`;

export const CREATE_PROJECT = gql`
  mutation CreateProject(
    $name: String!
    $description: String!
    $status: ProjectStatus!
    $polesetId: ID
  ) {
    createProject(
      name: $name
      description: $description
      status: $status
      polesetId: $polesetId
    ) {
      id
      name
      description
      status
      poleset {
        id
      }
    }
  }
`;

export const EDIT_PROJECT = gql`
  mutation EditProject(
    $projectId: ID!
    $newName: String!
    $newDescription: String!
    $newStatus: ProjectStatus!
    $newPolesetId: ID
  ) {
    editProject(
      projectId: $projectId
      newName: $newName
      newDescription: $newDescription
      newStatus: $newStatus
      newPolesetId: $newPolesetId
    ) {
      id
      name
      description
      status
      poleset {
        id
      }
    }
  }
`;

export const DELETE_PROJECT = gql`
  mutation DeleteProject($id: ID!) {
    deleteProject(id: $id) {
      id
    }
  }
`;

export const GET_PROJECTS_BY_STATUS = gql`
  query GetProjectsByStatus(
    $status: ProjectStatus!
    $pagination: CursorPaginationInput
  ) {
    getProjectsByStatus(status: $status, pagination: $pagination) {
      data {
        id
        description
        name
        status
        createdAt
        owner {
          id
          firstName
          lastName
        }
        poleset {
          id
        }
      }
      nextCursor
      hasMore
    }
  }
`;

export const PROJECTS = gql`
  query GetProjectsByUserId($userId: ID!) {
    getProjectsByUserId(userId: $userId) {
      id
      description
      name
      status
      createdAt
      owner {
        id
        firstName
        lastName
      }
      poleset {
        id
      }
    }
  }
`;

export const PROJECT = gql`
  ${PROJECT_FRAGMENT}
  query GetProjectById($id: ID!) {
    getProjectById(id: $id) {
      ...Project
    }
  }
`;

export const PROJECT_STATUS = gql`
  query GetProjectById($projectId: ID!) {
    getProjectById(id: $projectId) {
      id
      status
    }
  }
`;

export const SET_PROJECT_STATUS = gql`
  mutation SetProjectStatus($projectId: ID!, $status: ProjectStatus!) {
    setProjectStatus(projectId: $projectId, status: $status) {
      id
      status
    }
  }
`;

export const MOVE_POLE = gql`
  mutation MovePole($projectId: ID!, $pole: PoleInput!) {
    movePole(projectId: $projectId, pole: $pole) {
      id
      poles {
        identifier
        direction {
          y
          x
          z
        }
        length
        position {
          x
          y
          z
        }
      }
    }
  }
`;

export const ADD_ITEMS = gql`
  mutation AddItems(
    $projectId: ID!
    $poles: [PoleInput!]!
    $squareLashings: [LashingInput!]!
    $bipodLashings: [BipodLashingInput!]!
    $tripodLashings: [TripodLashingInput!]!
    $scaffoldLashings: [ScaffoldLashingInput!]!
  ) {
    addItems(
      projectId: $projectId
      poles: $poles
      squareLashings: $squareLashings
      bipodLashings: $bipodLashings
      tripodLashings: $tripodLashings
      scaffoldLashings: $scaffoldLashings
    ) {
      id
      poles {
        identifier
        direction {
          y
          x
          z
        }
        length
        position {
          x
          y
          z
        }
      }
      lashings {
        fixedPoleIdentifier
        identifier
        loosePoleIdentifier
      }
      bipodLashings {
        identifier
        pole1Identifier
        pole2Identifier
      }
      tripodLashings {
        identifier
        leftPoleIdentifier
        middlePoleIdentifier
        rightPoleIdentifier
      }
      scaffoldLashings {
        identifier
        pole1Identifier
        pole2Identifier
        offset
      }
    }
  }
`;

export const DELETE_ITEMS = gql`
  mutation DeleteItems(
    $projectId: ID!
    $poleIdentifiers: [String!]!
    $squareLashingIdentifiers: [String!]!
    $bipodLashingIdentifiers: [String!]!
    $tripodLashingIdentifiers: [String!]!
    $scaffoldLashingIdentifiers: [String!]!
  ) {
    deleteItems(
      projectId: $projectId
      poleIdentifiers: $poleIdentifiers
      squareLashingIdentifiers: $squareLashingIdentifiers
      bipodLashingIdentifiers: $bipodLashingIdentifiers
      tripodLashingIdentifiers: $tripodLashingIdentifiers
      scaffoldLashingIdentifiers: $scaffoldLashingIdentifiers
    ) {
      id
      poles {
        identifier
        direction {
          y
          x
          z
        }
        length
        position {
          x
          y
          z
        }
      }
      lashings {
        fixedPoleIdentifier
        identifier
        loosePoleIdentifier
      }
      bipodLashings {
        identifier
        pole1Identifier
        pole2Identifier
      }
      tripodLashings {
        identifier
        leftPoleIdentifier
        middlePoleIdentifier
        rightPoleIdentifier
      }
      scaffoldLashings {
        identifier
        pole1Identifier
        pole2Identifier
        offset
      }
    }
  }
`;

export const EDIT_LASHING = gql`
  mutation EditLashing($projectId: ID!, $lashing: LashingInput!) {
    editLashing(projectId: $projectId, lashing: $lashing) {
      id
      lashings {
        fixedPoleIdentifier
        identifier
        loosePoleIdentifier
      }
    }
  }
`;

export const EDIT_BIPOD_LASHING = gql`
  mutation EditBipodLashing(
    $projectId: ID!
    $bipodLashing: BipodLashingInput!
  ) {
    editBipodLashing(projectId: $projectId, bipodLashing: $bipodLashing) {
      id
      bipodLashings {
        identifier
        pole1Identifier
        pole2Identifier
      }
    }
  }
`;

export const EDIT_TRIPOD_LASHING = gql`
  mutation EditTripodLashing(
    $projectId: ID!
    $tripodLashing: TripodLashingInput!
  ) {
    editTripodLashing(projectId: $projectId, tripodLashing: $tripodLashing) {
      id
      tripodLashings {
        identifier
        leftPoleIdentifier
        middlePoleIdentifier
        rightPoleIdentifier
      }
    }
  }
`;

export const EDIT_SCAFFOLD_LASHING = gql`
  mutation EditScaffoldLashing(
    $projectId: ID!
    $scaffoldLashing: ScaffoldLashingInput!
  ) {
    editScaffoldLashing(
      projectId: $projectId
      scaffoldLashing: $scaffoldLashing
    ) {
      id
      scaffoldLashings {
        identifier
        pole1Identifier
        pole2Identifier
        offset
      }
    }
  }
`;

export const PROJECT_STATS = gql`
  query GetProjectStats {
    getProjectStats {
      total
      byStatus {
        PRIVATE
        SHARED
        PUBLIC
      }
    }
  }
`;

export const TOP_PROJECTS_BY_POLES = gql`
  query GetTopProjectsByPoles($limit: Int = 10) {
    getTopProjectsByPoles(limit: $limit) {
      id
      name
      status
      polesCount
      owner
      ownerId
      createdAt
      updatedAt
    }
  }
`;
